import React, { useCallback, useEffect, useRef, useState } from "react";
import { Switch, Route, Redirect, useHistory, useLocation, matchPath } from "react-router-dom";
import { supabase } from "utils/supabase";
// components

import AdminNavbar from "components/Navbars/AdminNavbar.js";
import HeaderStats from "components/Headers/HeaderStats.js";
import MobileHeaderStats from "components/Headers/MobileHeaderStats.js";
import FooterAdmin from "components/Footers/FooterAdmin.js";
import BackdropAlert from "components/Custom/BackdropAlert.js";
import LogoLoader from "components/Custom/LogoLoader.js";

// views

import Dashboard from "views/admin/Dashboard.js";
import Examenes from "views/admin/Examenes.js";
import Simulador from "views/admin/Simulador.js";
import Resultados from "views/admin/Resultados.js";
import Preguntas from "views/admin/Preguntas.js";
import Practica from "views/admin/Practica.js";
import SesionPractica from "views/admin/SesionPractica.js";
import PracticaResultados from "views/admin/PracticaResultados.js";
import PracticaPreguntas from "views/admin/PracticaPreguntas.js";
import Flashcards from "views/admin/Flashcards.js";
import SesionFlashcards from "views/admin/SesionFlashcards.js";
import ResultadosFlashcards from "views/admin/ResultadosFlashcards.js";
import Estadisticas from "views/admin/Estadisticas.js";
import Actividad from "views/admin/Actividad.js";
import DesempenoEspecialidad from "views/admin/DesempenoEspecialidad.js";
import PlanEstudio from "views/admin/PlanEstudio.js";
import MiCuenta from "views/admin/MiCuenta.js";
import RetoInvite from "views/admin/RetoInvite.js";
import RetoLeaderboard from "views/admin/RetoLeaderboard.js";
import Retos from "views/admin/Retos.js";
import { startDeviceSession, sendDeviceHeartbeat } from "utils/devices";

export default function Admin() {
	const history = useHistory();
	const location = useLocation();
	const heartbeatIntervalRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [hasAccess, setHasAccess] = useState(false);
	const [deviceBlocked, setDeviceBlocked] = useState(false);
	const [activeDevice, setActiveDevice] = useState(null);
	const [forcingDevice, setForcingDevice] = useState(false);
	const [deviceActionError, setDeviceActionError] = useState("");
	const [showSplash, setShowSplash] = useState(true);
	const [isMobileViewport, setIsMobileViewport] = useState(() =>
		typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
	);

	useEffect(() => {
		const mediaQuery = window.matchMedia("(max-width: 767px)");
		const updateViewport = (event) => setIsMobileViewport(event.matches);

		setIsMobileViewport(mediaQuery.matches);
		mediaQuery.addEventListener?.("change", updateViewport);
		return () => mediaQuery.removeEventListener?.("change", updateViewport);
	}, []);

	const stopHeartbeat = useCallback(() => {
		if (heartbeatIntervalRef.current) {
			clearInterval(heartbeatIntervalRef.current);
			heartbeatIntervalRef.current = null;
		}
	}, []);

	const startHeartbeat = useCallback(() => {
		stopHeartbeat();

		heartbeatIntervalRef.current = setInterval(async () => {
			const heartbeat = await sendDeviceHeartbeat();

			if (!heartbeat?.allowed) {
				setDeviceBlocked(true);
				setActiveDevice(heartbeat?.active_device || null);
				stopHeartbeat();
			}
		}, 90000);
	}, [stopHeartbeat]);

	const refreshDeviceSession = useCallback(
		async (force = false) => {
			const result = await startDeviceSession(force);

			if (result?.allowed) {
				setDeviceBlocked(false);
				setActiveDevice(null);
				setDeviceActionError("");
				startHeartbeat();
				return result;
			}

			setDeviceBlocked(true);
			setActiveDevice(result?.active_device || null);
			stopHeartbeat();
			return result;
		},
		[startHeartbeat, stopHeartbeat]
	);

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [location.pathname]);

	useEffect(() => {
		const checkAccess = async () => {
			const {
				data: { user },
			} = await supabase.auth.getUser();

			if (!user) {
				setHasAccess(false);
				setLoading(false);
				return;
			}

			const { data, error } = await supabase.rpc("get_billing_status");

			if (error) {
				console.error(error);
				setHasAccess(false);
				setLoading(false);
				return;
			}

			setHasAccess(Boolean(data?.has_pro_access));
			setLoading(false);
		};

		checkAccess();
	}, []);

	useEffect(() => {
		const initDeviceSession = async () => {
			await refreshDeviceSession(false);
		};

		if (!loading && hasAccess) {
			initDeviceSession();
		}

		return () => {
			stopHeartbeat();
		};
	}, [loading, hasAccess, refreshDeviceSession, stopHeartbeat]);

	useEffect(() => {
		if (loading) return undefined;

		// let the logo roar/burst animation finish before revealing the app
		const timeoutId = setTimeout(() => setShowSplash(false), 950);
		return () => clearTimeout(timeoutId);
	}, [loading]);

	if (showSplash) {
		return <LogoLoader done={!loading} />;
	}

	if (!hasAccess) {
		return <Redirect to="/auth/pagos" />;
	}

	const handleUseThisDevice = async () => {
		setForcingDevice(true);
		setDeviceActionError("");

		const result = await refreshDeviceSession(true);

		if (!result?.allowed) {
			setDeviceActionError("No pudimos activar este dispositivo. Intenta nuevamente.");
		}

		setForcingDevice(false);
	};

	const handleSignOut = async () => {
		await supabase.auth.signOut();
		stopHeartbeat();
		history.push("/auth/login");
	};

	// list routes where HeaderStats should be shown
	const showHeaderRoutes = [
		"/admin/dashboard",
		// add more exact paths here, e.g. "/admin/maps"
	];
	const showHeader = showHeaderRoutes.some((p) =>
		Boolean(matchPath(location.pathname, { path: p, exact: true }))
	);

	// routes where navbar should overlap the content (use negative margin)
	const overlapRoutes = ["/admin/dashboard"];
	const isOverlap = !isMobileViewport && overlapRoutes.some((p) =>
		Boolean(matchPath(location.pathname, { path: p, exact: true }))
	);
	const containerClass = isMobileViewport && showHeader
		? "px-4 md:px-10 mx-auto w-full pt-0 relative z-10"
		: isOverlap
		? "px-4 md:px-10 mx-auto w-full pt-0 relative z-10 dashboard-overlap"
		: "px-4 md:px-10 mx-auto w-full pt-24";
	const containerStyle = undefined;

	return (
		<>
			<style>
				{`
					.dashboard-overlap {
						margin-top: -56px;
					}

					@media (min-width: 768px) {
						.dashboard-overlap {
							margin-top: -100px;
						}
					}
				`}
			</style>
				<AdminNavbar />
				{/* Header */}
				{showHeader && (
					<div style={isMobileViewport ? { paddingTop: 64 } : undefined}>
						{isMobileViewport ? <MobileHeaderStats adminSpacing showLogo={false} /> : <HeaderStats />}
					</div>
				)}
				<div className={containerClass} style={containerStyle}>
					<Switch>
						<Route path="/admin/dashboard" exact component={Dashboard} />
						<Route path="/admin/examenes" exact component={Examenes} />
						<Route path="/admin/simulador/:examId" exact component={Simulador} />
						<Route path="/admin/simulador/:examId/resultados" exact component={Resultados} />
						<Route path="/admin/simulador/:examId/preguntas/:questionId?" exact component={Preguntas} />
						<Route path="/admin/practica" exact component={Practica} />
						<Route path="/admin/practica/:sessionId" exact component={SesionPractica} />
						<Route path="/admin/practica/:sessionId/resultados" exact component={PracticaResultados} />
						<Route path="/admin/practica/:sessionId/preguntas/:questionId?" exact component={PracticaPreguntas} />
						<Route path="/admin/flashcards" exact component={Flashcards} />
						<Route path="/admin/flashcards/:sessionId" exact component={SesionFlashcards} />
						<Route path="/admin/flashcards/:sessionId/resultados" exact component={ResultadosFlashcards} />
						<Route path="/admin/estadisticas" exact component={Estadisticas} />
						<Route path="/admin/actividad" exact component={Actividad} />
						<Route path="/admin/plan-estudio" exact component={PlanEstudio} />
						<Route path="/admin/especialidades" exact component={DesempenoEspecialidad} />
						<Route path="/admin/mi-cuenta" exact component={MiCuenta} />
						<Route path="/admin/cuenta" exact component={MiCuenta} />
						<Route path="/admin/reto/:inviteCode/resultados" exact component={RetoLeaderboard} />
						<Route path="/admin/reto/:inviteCode" exact component={RetoInvite} />
						<Route path="/admin/retos" exact component={Retos} />
						<Redirect from="/admin" to="/admin/dashboard" />
					</Switch>
					<FooterAdmin />
				</div>

				<BackdropAlert
					open={deviceBlocked}
					closeOnBackdropClick={false}
					severity="warning"
					title="Cuenta activa en otro dispositivo"
					message="ENARM Apex solo permite un dispositivo activo a la vez."
					description={
						deviceActionError ||
						(activeDevice
							? `Ultimo dispositivo activo: ${activeDevice.device_name || "Dispositivo desconocido"}`
							: undefined)
					}
					backdropSx={{ zIndex: 2000 }}
					actions={[
						{
							key: "use-device",
							label: forcingDevice ? "Validando..." : "Usar este dispositivo",
							onClick: handleUseThisDevice,
							variant: "contained",
							color: "primary",
							disabled: forcingDevice,
						},
						{
							key: "sign-out",
							label: "Cerrar sesión",
							onClick: handleSignOut,
							variant: "outlined",
							color: "inherit",
							disabled: forcingDevice,
						},
					]}
				/>
		</>
	);
}
